import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SECTORS, SECTORS_BY_SLUG } from "@/lib/sectors";
import { Check, ArrowRight, X, Bot, Sparkles, HelpCircle } from "lucide-react";
import BrandMark from "@/components/BrandMark";

const Section = ({ children, className = "" }) => (
  <section className={`max-w-6xl mx-auto px-6 ${className}`}>{children}</section>
);

export default function SectorLanding() {
  const { slug } = useParams();
  const s = SECTORS_BY_SLUG[slug];

  // SEO por página: título, descripción, canonical y datos estructurados FAQ.
  useEffect(() => {
    if (!s) return;
    const prevTitle = document.title;
    document.title = s.title || `IA para ${s.name} · Agent Company OS`;

    const setMeta = (sel, attr, val, create) => {
      let el = document.head.querySelector(sel);
      if (!el && create) { el = create(); document.head.appendChild(el); }
      const prev = el ? el.getAttribute(attr) : null;
      if (el && val) el.setAttribute(attr, val);
      return [el, prev];
    };
    const [descEl, prevDesc] = setMeta('meta[name="description"]', "content", s.meta,
      () => { const m = document.createElement("meta"); m.setAttribute("name", "description"); return m; });
    const [canEl, prevCan] = setMeta('link[rel="canonical"]', "href", `https://aicompany.beautyos.es/para/${s.slug}`,
      () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; });

    document.getElementById("faq-schema")?.remove();
    const faq = document.createElement("script");
    faq.type = "application/ld+json";
    faq.id = "faq-schema";
    faq.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (s.faqs || []).map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(faq);

    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc) descEl.setAttribute("content", prevDesc);
      if (canEl && prevCan) canEl.setAttribute("href", prevCan);
      document.getElementById("faq-schema")?.remove();
    };
  }, [slug, s]);

  if (!s) return <Navigate to="/" replace />;

  const otros = SECTORS.filter((x) => x.slug !== slug).slice(0, 8);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-[var(--border)] bg-[var(--bg)]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark className="w-8 h-8" />
            <div className="leading-none">
              <div className="font-display font-black text-[15px] tracking-tight">AGENT COMPANY OS</div>
              <div className="label-mono mt-0.5">por BeautyOS</div>
            </div>
          </Link>
          <nav className="flex items-center gap-2 md:gap-4">
            <Link to="/precios" className="hidden sm:block text-sm text-[var(--text-dim)] hover:text-white">Precios</Link>
            <Link to="/login" className="text-sm text-[var(--text-dim)] hover:text-white">Entrar</Link>
            <Link to="/register">
              <Button className="rounded-none h-9 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold text-sm">
                Empezar gratis
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <div className="relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <Section className="relative z-10 py-20 md:py-24">
          <div className="inline-flex items-center gap-2 border border-[var(--border)] px-3 py-1 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[var(--accent)]" />
            <span className="label-mono">IA para {s.name}</span>
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl leading-[0.98] tracking-tight mb-6 max-w-4xl">
            {s.h1}
          </h1>
          <p className="text-lg text-[var(--text-dim)] leading-relaxed mb-8 max-w-2xl">{s.sub}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/register">
              <Button className="rounded-none h-12 px-6 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold text-base">
                Empezar gratis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/precios">
              <Button variant="outline" className="rounded-none h-12 px-6 border-[var(--border)] hover:border-[var(--accent)] text-base">
                Ver planes
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-6 text-xs text-[var(--text-dim)]">
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--good)]" /> Sin tarjeta</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--good)]" /> Listo en una tarde</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[var(--good)]" /> Tú apruebas cada acción sensible</span>
          </div>
        </Section>
      </div>

      {/* BENEFICIO */}
      {s.beneficio && (
        <div className="border-b border-[var(--accent)]/40 bg-[var(--accent)]/[0.06]">
          <Section className="py-7">
            <p className="text-center font-display font-bold text-xl md:text-2xl tracking-tight">{s.beneficio}</p>
          </Section>
        </div>
      )}

      {/* PAINS + DOES */}
      <Section className="py-20">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="border border-[var(--border)] bg-[var(--surface)] p-7">
            <div className="label-mono mb-5">Lo que te pasa hoy</div>
            <ul className="space-y-4">
              {s.pains.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <X className="w-5 h-5 text-[var(--danger)] mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span className="text-[var(--text-dim)]">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border border-[var(--accent)] bg-[var(--surface)] p-7">
            <div className="label-mono mb-5 text-[var(--accent)]">Lo que hace por ti</div>
            <ul className="space-y-4">
              {s.does.map((d) => (
                <li key={d} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[var(--good)] mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* AGENTES */}
      <div className="border-y border-[var(--border)] bg-[var(--surface)]/40">
        <Section className="py-16">
          <div className="text-center mb-10">
            <div className="label-mono mb-3">Tu equipo para {s.name.toLowerCase()}</div>
            <h2 className="font-display font-black text-3xl tracking-tight">Agentes especializados, listos desde el minuto uno</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {s.agents.map((a) => (
              <div key={a} className="border border-[var(--border)] bg-[var(--bg)] p-5 text-center hover-neon">
                <div className="w-10 h-10 border border-[var(--accent)] flex items-center justify-center mx-auto mb-3">
                  <Bot className="w-5 h-5 text-[var(--accent)]" strokeWidth={2} />
                </div>
                <div className="font-semibold text-sm">{a}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[var(--text-dim)] mt-6">
            Y puedes añadir, quitar o editar los que quieras. Tú mandas.
          </p>
        </Section>
      </div>

      {/* CÓMO FUNCIONA */}
      <Section className="py-20">
        <div className="text-center mb-12">
          <div className="label-mono mb-3">En 3 pasos</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Empieza hoy mismo</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { n: "01", t: "Elige tu plantilla", d: `Selecciona ${s.name} y tienes tu equipo de agentes listo.` },
            { n: "02", t: "Ajústalo a tu negocio", d: "Pon tus horarios, servicios y tono. Añade o quita agentes." },
            { n: "03", t: "Da permisos y a trabajar", d: "Decide qué hace cada agente solo y qué necesita tu OK." },
          ].map((step) => (
            <div key={step.n} className="border border-[var(--border)] bg-[var(--surface)] p-6">
              <div className="font-display font-black text-5xl text-[var(--accent)]/30 mb-3">{step.n}</div>
              <h3 className="font-display font-bold text-lg mb-2 tracking-tight">{step.t}</h3>
              <p className="text-sm text-[var(--text-dim)] leading-relaxed">{step.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      {s.faqs && s.faqs.length > 0 && (
        <div className="border-y border-[var(--border)] bg-[var(--surface)]/40">
          <Section className="py-20">
            <div className="text-center mb-12">
              <div className="label-mono mb-3 flex items-center justify-center gap-2"><HelpCircle className="w-3.5 h-3.5" /> Preguntas frecuentes</div>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">Lo que te preguntas antes de empezar</h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {s.faqs.map((f) => (
                <div key={f.q} className="border border-[var(--border)] bg-[var(--bg)] p-5">
                  <div className="font-semibold mb-1.5">{f.q}</div>
                  <p className="text-sm text-[var(--text-dim)] leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </Section>
        </div>
      )}

      {/* CTA */}
      <div className="border-t border-[var(--border)] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <Section className="relative z-10 py-20 text-center">
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight mb-5">
            Pon a trabajar la IA en tu {s.name.toLowerCase().replace(/s$/, "")}.
          </h2>
          <p className="text-[var(--text-dim)] text-lg mb-8 max-w-xl mx-auto">
            Empieza gratis, sin tarjeta. En una tarde tienes tu equipo atendiendo.
          </p>
          <Link to="/register">
            <Button className="rounded-none h-12 px-8 bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 font-semibold text-base">
              Empezar gratis <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </Section>
      </div>

      {/* OTROS SECTORES */}
      <div className="border-t border-[var(--border)] bg-[var(--surface)]">
        <Section className="py-10">
          <div className="label-mono mb-4 text-center">También para</div>
          <div className="flex flex-wrap justify-center gap-2.5">
            {otros.map((o) => (
              <Link key={o.slug} to={`/para/${o.slug}`}
                    className="border border-[var(--border)] px-3 py-1.5 text-sm text-[var(--text-dim)] hover-neon hover:text-white">
                {o.name}
              </Link>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-[var(--border)] text-center text-xs text-[var(--text-dim)]">
            © 2026 BeautyOS · Agent Company OS · <Link to="/" className="hover:text-white underline">Inicio</Link>
          </div>
        </Section>
      </div>
    </div>
  );
}
